import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

/**
 * Shared recovery dialog for Missing & Damaged (linen + assets).
 * onSubmit receives: { recovery_reason, location_found, destination, room_id, recovered_qty, proof_url }
 * Props:
 *  - title, maxQty
 *  - destinationOptions: [{value,label}] (empty => hide destination)
 *  - roomOption: destination value that needs a room, e.g. "ROOM_CLEAN" or "A_ROOM"
 *  - rooms: [{id, number}]
 */
export default function RecoveryDialog({ open, onOpenChange, title, maxQty = 1, destinationOptions = [], roomOption, rooms = [], onSubmit }) {
  const [reason, setReason] = useState("");
  const [locationFound, setLocationFound] = useState("");
  const [destination, setDestination] = useState(destinationOptions[0]?.value || "");
  const [roomId, setRoomId] = useState("");
  const [qty, setQty] = useState(String(maxQty || 1));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(""); setLocationFound(""); setDestination(destinationOptions[0]?.value || "");
      setRoomId(""); setQty(String(maxQty || 1)); setFile(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, maxQty]);

  const needsRoom = roomOption && destination === roomOption;

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) { toast.error("Enter a recovery reason (min 3 chars)"); return; }
    const q = Number(qty);
    if (!/^\d+$/.test(String(qty)) || q < 1 || q > maxQty) { toast.error(`Recovered quantity must be a whole number between 1 and ${maxQty}`); return; }
    if (destinationOptions.length > 0 && !destination) { toast.error("Choose a destination"); return; }
    if (needsRoom && !roomId) { toast.error("Choose a destination room"); return; }
    setBusy(true);
    try {
      let proof_url = null;
      if (file) {
        if (!file.type.startsWith("image/")) { toast.error("Proof must be an image"); setBusy(false); return; }
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        proof_url = data.url;
      }
      await onSubmit({
        recovery_reason: reason.trim(),
        location_found: locationFound.trim() || null,
        destination: destinationOptions.length ? destination : null,
        room_id: needsRoom ? roomId : null,
        recovered_qty: q,
        proof_url,
      });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Recovered Quantity <span className="text-slate-400 text-xs">(of {maxQty})</span></Label>
            <Input type="number" min="1" max={maxQty} step="1" value={qty} onChange={e => setQty(e.target.value)} className="h-10 mt-1" data-testid="recovery-qty" />
            {Number(qty) < maxQty && <div className="text-xs text-amber-600 mt-1">Partial recovery — the rest stays open/in holding.</div>}
          </div>
          <div>
            <Label>Recovery Reason *</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="How/why it was recovered" className="h-10 mt-1" data-testid="recovery-reason" />
          </div>
          <div>
            <Label>Location Found</Label>
            <Input value={locationFound} onChange={e => setLocationFound(e.target.value)} placeholder="e.g. Room 204 wardrobe, laundry van" className="h-10 mt-1" data-testid="recovery-location" />
          </div>
          {destinationOptions.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Destination</Label>
                <Select value={destination} onValueChange={setDestination}>
                  <SelectTrigger className="h-10 mt-1" data-testid="recovery-destination"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{destinationOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {needsRoom && (
                <div>
                  <Label>Room</Label>
                  <Select value={roomId} onValueChange={setRoomId}>
                    <SelectTrigger className="h-10 mt-1" data-testid="recovery-room"><SelectValue placeholder="Room" /></SelectTrigger>
                    <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <div>
            <Label>Image Proof <span className="text-slate-400 text-xs">(optional, images only)</span></Label>
            <Input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} className="h-10 mt-1" data-testid="recovery-proof" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={submit} disabled={busy} data-testid="recovery-submit">Confirm Recovery</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
